import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";


// Helper function to format dates
const formatDate = (timestamp, options = {}) => {
  if (timestamp === null || timestamp === undefined) return '';
  
  // Convert to number if it's a string
  const numTimestamp = Number(timestamp);
  if (isNaN(numTimestamp)) return String(timestamp); // Return original if not a valid number
  
  try {
    // Determine if timestamp is in seconds (likely if > 01/01/2000 but < 01/01/2030 in seconds)
    let date;
    if (numTimestamp > 946684800 && numTimestamp < 1893456000) {
      // This appears to be a timestamp in seconds
      date = new Date(numTimestamp * 1000);
    } else {
      // Assume it's in milliseconds
      date = new Date(numTimestamp);
    }
    
    // Return empty string for invalid dates
    if (isNaN(date.getTime())) return '';
    
    // Default format options - changed dateStyle to 'medium' for full year
    const defaultOptions = {
      dateStyle: 'medium', // This will show "Jul 9, 2025" format
      timeStyle: 'medium',
      hour12: false,
    };
    
    // Merge with custom options
    const formatOptions = { ...defaultOptions, ...options };
    
    return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
  } catch (e) {
    console.warn('Date formatting error:', e);
    return String(timestamp); // return original if formatting fails
  }
};

export const exportToFile = async ({ data, columns, fileType, title, landscape = true,allowEmpty }) => {
  try {
    // Validate inputs
    if ((!Array.isArray(data) || data.length === 0) && !allowEmpty) {
      throw new Error("No data to export");
    }

    if (!Array.isArray(columns) || columns.length === 0) {
      throw new Error("No columns configured");
    }

    // Format data according to column configuration
  const formattedData = data.map(item => {
  const formattedItem = {};

  columns.forEach(col => {
    try {
      // Handle nested properties with dot notation
      const value = col.field.split('.').reduce(
        (obj, key) => (obj && obj[key] !== undefined) ? obj[key] : null,
        item
      );

      formattedItem[col.header] = col.formatter
        ? col.formatter(value, item)
        : col.type === 'date'
          ? formatDate(value, col.formatOptions)
          : col.type === 'boolean'
            ? (value ? 'Yes' : 'No')
            : col.type === 'number'
              ? (value !== null && value !== undefined && value !== '' 
                  ? Number(value) 
                  : 0)
              : value;

    } catch (e) {
      console.warn(`Error formatting column ${col.header}:`, e);
      formattedItem[col.header] = col.type === 'number' ? 0 : '';
    }
  });

  return formattedItem;
});

    console.log("formatedData",formattedData);
    if (fileType.toUpperCase() === "PDF") {
      // Create PDF document
      const doc = new jsPDF({
        orientation: landscape ? 'landscape' : 'portrait'
      });

      // Add title and generation date
      doc.setFontSize(16);
      doc.text(title, 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);

      // Prepare table data
      const tableData = formattedData.map(item => 
        columns.map(col => item[col.header])
      );

      // Prepare headers
      const headers = columns.map(col => col.header);

      // Calculate available width (subtract left and right margins)
      const pageWidth = doc.internal.pageSize.getWidth();
      const availableWidth = pageWidth - 20; // 10px margin on each side

      // Calculate column widths
      const columnCount = columns.length;
      const columnStyles = {};
      let totalDefinedWidth = 0;
      let autoWidthColumns = 0;

      // First pass: calculate defined widths and count auto-width columns
      columns.forEach((col, index) => {
        if (col.width && typeof col.width === 'number') {
          columnStyles[index] = { cellWidth: col.width };
          totalDefinedWidth += col.width;
        } else {
          autoWidthColumns++;
        }
      });

      // Calculate remaining width for auto-width columns
      const remainingWidth = availableWidth - totalDefinedWidth;
      const autoWidth = remainingWidth / autoWidthColumns;

      // Second pass: set widths for auto-width columns
      columns.forEach((col, index) => {
        if (!col.width || typeof col.width !== 'number') {
          columnStyles[index] = { 
            cellWidth: autoWidth,
            ...(col.cellStyles || {})
          };
        }
      });

      // Configure table styles
      const tableStyles = {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
      };

      autoTable(doc, {
        head: [headers],
        body: tableData,
        startY: 25,
        margin: { left: 10, right: 10 },
        styles: tableStyles,
        columnStyles,
        didDrawPage: function (data) {
          // Add page footer
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            data.settings.margin.left,
            doc.internal.pageSize.height - 10
          );
        },
        // Enable horizontal page breaks if needed
        horizontalPageBreak: true,
        // Shrink table to fit if it's slightly too large
        tableWidth: 'auto',
        // Wrap long text
        styles: { 
          ...tableStyles,
          overflow: 'linebreak',
          minCellHeight: 8
        }
      });

      // Save PDF file
      doc.save(`${title.replace(/[^a-z0-9]/gi, '_')}.pdf`);

    } else if(fileType.toUpperCase() === "CSV"){
      // CSV Export
      const escapeCsvValue = (value) => {
        if (value == null) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      const headers = columns.map(col => escapeCsvValue(col.header));
      const csvRows = formattedData.map(item =>
        columns.map(col => escapeCsvValue(item[col.header])).join(',')
      );

      const csvContent = [headers.join(','), ...csvRows].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${title.replace(/[^a-z0-9]/gi, '_')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }else if(fileType.toUpperCase() === "EXCEL"){
      // Excel Export using xlsx library
      
      // Prepare worksheet data
      const excelData = [
        // Header row
        columns.map(col => col.header),
        // Data rows
        ...formattedData.map(item => 
          columns.map(col => item[col.header])
        )
      ];
      
      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      
      // Generate Excel file and download
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${title.replace(/[^a-z0-9]/gi, '_')}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    console.error('Export error:', error);
    throw error;
  }
};